import { createStore } from 'vuex'
import { lang } from '../plugins/utils'
import { getTextData, getAuthInfo, getWalletList, getWalletUserNotifications, getExchangeRates, doExchange } from '../services/api'
import { handleAsyncError } from '../services/errorHandler'
import {computed, onMounted} from "vue";

async function refreshWalletList(context) {

  const walletList = await getWalletList(context.state.authToken)
  context.commit("updateWalletList", walletList)

  return walletList
}

const store = createStore({
  state: {
    sendConfirmCode: false,
    phone: '',
    authToken: localStorage.getItem("authToken"),
    walletList: null,  
    authInfo: null,
    authInfoLoading: false,
    textData: null,
    userNotifications: null,
    openSearchAssets: false,
    openSelectAccount: false,
    selectExchangeOpened: false,
    selectExchangeActiveTab: null,
    accountsLoading: true,
    exchangeFromAccount: null,
    exchangeToAccount: null,
    exchangeRates: null,
    lang: lang,
    currentWallet: null,
  },
  getters: {
    authInfoLoading: state => !!state.authInfoLoading,
    hasAuthInfo: state => !!state.authInfo,
    kycPassed: (state) => {
      return !!state.authInfo && state.authInfo.identification_level.level !== 0
    },
    textData: (state) => (textId) => {
      if (!textId || !state.textData || !state.textData[textId] || !state.lang) {
        return null;
      }
      return state.textData[textId][state.lang];
    },
    activesByCurrencies: state => {
      if (!state.walletList) return []

      if (state.exchangeRates) {
        // const exchangeRates = [
        //   ...state.exchangeRates.rates,
        //   {
        //     "id": "0195f72b-0ed4-7cb8-5c11-a33acec35a62",
        //     "from_currency_code": "USDC",
        //     "to_currency_code": "EUR",
        //     "quantity_in": "1.0000",
        //     "quantity_out": "0.917",
        //     "min_amount": "0.0000",
        //     "max_amount": "0.0000"
        //   }
        // ]

        const exchangeRates = state.exchangeRates.rates
        return state.walletList.reduce((acc, item) => {
          const { currency_code } = item
          const ratesForCurrency = exchangeRates.reduce((acc, item) => {
            if (item.from_currency_code === currency_code) {
              acc++
            }

            return acc
          }, 0)

          if (ratesForCurrency === state.walletList.length - 1) {
            const amount = state.walletList.reduce((acc, item) => {
              if (item.currency_code === currency_code) {
                acc += Number(item.balance)
              } else {
                const rate = exchangeRates.find(rateItem => {
                  return rateItem.from_currency_code === currency_code && rateItem.to_currency_code === item.currency_code
                })

                acc += Number(item.balance * rate.quantity_out)
              }
              return acc
            }, 0)
            acc.push({
              id: item.id,
              amount,
              currency_code
            })
          }
          return acc
        }, [])
      } else {
        return []
      }
    }
  },
  mutations: {
    changeSearchAssets(state, value) {
      state.openSearchAssets = value
    },
    changeSelectAccount(state, value) {
      state.openSelectAccount = value
    },
    selectExchangeActiveTab(state, value) {
      state.selectExchangeActiveTab = value
    },
    changeSelectExchange(state,value) {
      state.selectExchangeOpened = value      
    },    
    logout(state) {
      state.authToken = null
      state.authInfo = null
      localStorage.removeItem('authToken')
    },      
    setAuthToken(state, authToken) {
      state.authToken = authToken
      localStorage.setItem('authToken', authToken)
    },
    ConfirmCode(state) {
      state.sendConfirmCode = !state.sendConfirmCode
    },
    changePhoneNumber (state, phone) {
      state.phone = phone
    },
    // API update
    updateWalletList(state, walletList) {
      if (!!state.walletList) return

      state.walletList = walletList
    },
    updateAuthInfo(state, authInfo) {
      state.authInfo = authInfo
    },
    updateTextData(state, textData) {
      state.textData = textData
    },
    updateUserNotifications(state, userNotifications) {
      state.userNotifications = userNotifications
    },

    // API Update: exchange
    setAccountsIsFetched(state) {
      state.accountsLoading = false
    },
    updateExchangeFromAccount(state, exchangeFromAccount) {
      state.exchangeFromAccount = exchangeFromAccount
    },
    updateExchangeToAccount(state, exchangeToAccount) {
      state.exchangeToAccount = exchangeToAccount
    },
    updateExchangeRates(state, exchangeRates) {
      state.exchangeRates = exchangeRates
    },
    updateCurrentWallet(state, currentWallet) {
      state.currentWallet = currentWallet
    },
  },
  actions: {    
    // API requests
    async refreshWalletList(context) {
      handleAsyncError( 
        context,        
        async function() {
          await refreshWalletList(context)
         }
      ) ()
    },
    async refreshExchangeRates(context) {
      handleAsyncError( 
        context,
        async function() {      
          context.commit("updateExchangeRates", await getExchangeRates(context.state.authToken))  
        }
      ) ()
    },    
    async refreshAuthInfo(context) {
      this.state.authInfoLoading = true
      handleAsyncError(
        context,
        async ()=> {
          context.commit("updateAuthInfo", await getAuthInfo(context.state.authToken).finally(() => this.state.authInfoLoading = false))
        }
      ) ()
    },
    async refreshTextData(context) {
      handleAsyncError( 
        context,
        async function() {      
            context.commit("updateTextData", await getTextData(context.state.authToken))  
        }
      ) ()
    },    
    async refreshUserNotifications(context) {
      handleAsyncError( 
        context,
        async function() {
          context.commit("updateUserNotifications", await getWalletUserNotifications(context.state.authToken))  
        }
      ) ()
    },
    async setDefaultExchangeAccounts(context) {
      handleAsyncError( 
        context,
        async function() {

          const walletList = (
            context.state.walletList === null 
            ? await refreshWalletList(context)
            : context.state.walletList
          )

          if (walletList && walletList.length > 1) {
            const firstAccount = walletList[0];
            const secondAccount = walletList[1];

            context.commit("updateExchangeFromAccount", firstAccount);
            context.commit("updateExchangeToAccount", secondAccount);
          }

          context.commit("setAccountsIsFetched");
        }
      ) ()
    },
    async doExchange(context, payload) {
      return handleAsyncError( 
        context,
        async function() {
          return await doExchange(
            context.state.authToken, 
            payload.from_account_id,
            payload.to_account_id,
            payload.from_amount,
            payload.to_amount,
            payload.rate_id
          )
        }
      ) ()
    }
  },
  modules: {
  }
})

export default store;
